// src/components/MfaEnrollment.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { auth, db } from '../firebase';
import { collection, query, where, getDocs, or } from 'firebase/firestore';
import {
    multiFactor,
    TotpMultiFactorGenerator,
    PhoneAuthProvider,
    PhoneMultiFactorGenerator,
    RecaptchaVerifier
} from 'firebase/auth';
import { useToast } from '../Toast/ToastContainer';

const MfaEnrollment = () => {
    // General States
    const [loading, setLoading] = useState(false);
    const [initializing, setInitializing] = useState(true);
    const [activeView, setActiveView] = useState('totp'); // 'totp', 'sms-options', 'sms-verify'
    const [verificationCode, setVerificationCode] = useState('');

    // TOTP (Authenticator) States
    const [totpSecret, setTotpSecret] = useState(null);
    const [qrCodeUrl, setQrCodeUrl] = useState('');

    // SMS States
    const [clientPhone, setClientPhone] = useState(null); // The raw formatted number for Firebase
    const [maskedPhone, setMaskedPhone] = useState(null); // The safe display string (e.g., ***-***-8956)
    const [verificationId, setVerificationId] = useState('');

    const navigate = useNavigate();
    const { addToast } = useToast();

    // 1. Initial Load: Fetch User, Generate TOTP, Fetch CRM Phone Number
    useEffect(() => {
        const initializeSetup = async () => {
            try {
                const user = auth.currentUser;
                if (!user) return navigate('/portal-login');

                // Force fetch latest security data from server to prevent caching issues
                await user.reload();

                if (user.multiFactor?.enrolledFactors?.length > 0) {
                    return navigate('/portal-dashboard');
                }

                // --- A. Setup Primary Option: Authenticator App ---
                const multiFactorSession = await multiFactor(user).getSession();
                const secret = await TotpMultiFactorGenerator.generateSecret(multiFactorSession);
                setTotpSecret(secret);
                setQrCodeUrl(secret.generateQrCodeUrl(user.email, "NVS Tax Portal"));

                // --- B. Setup Secondary Option: Fetch Phone from CRM ---
                const userEmail = (user.email || '').toLowerCase();
                const q = query(
                    collection(db, 'clients'),
                    or(
                        where('email', '==', userEmail),
                        where('authorizedEmails', 'array-contains', userEmail)
                    )
                );
                const querySnapshot = await getDocs(q);

                if (!querySnapshot.empty) {
                    const clientData = querySnapshot.docs[0].data();
                    const rawPhone = clientData.phone1 || clientData.phone || clientData.corpPhone;

                    if (rawPhone) {
                        // Clean the phone string (strip dashes, spaces, parentheses)
                        const numericPhone = rawPhone.replace(/\D/g, '');
                        // Format for Firebase (assuming NA numbers, prepend +1 if missing)
                        const formattedPhone = numericPhone.length === 10 ? `+1${numericPhone}` : `+${numericPhone}`;
                        const last4 = numericPhone.slice(-4);

                        setClientPhone(formattedPhone);
                        setMaskedPhone(`***-***-${last4}`);
                    }
                }

            } catch (error) {
                console.error("Initialization Error:", error);
                addToast("Failed to initialize secure setup.", "error");
            } finally {
                setInitializing(false);
            }
        };

        initializeSetup();
    }, [navigate, addToast]);

    // 2. Setup Invisible reCAPTCHA (Required by Firebase to prevent SMS spam)
    const setupRecaptcha = () => {
        if (!window.recaptchaVerifier) {
            window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
                'size': 'invisible',
                'callback': () => {
                    // reCAPTCHA solved
                }
            });
        }
    };

    // 3. Handle Authenticator App (TOTP) Verification
    const handleVerifyTotp = async (e) => {
        e.preventDefault();
        if (verificationCode.length !== 6 || !totpSecret) return;

        setLoading(true);
        try {
            const user = auth.currentUser;
            const assertion = TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, verificationCode);
            await multiFactor(user).enroll(assertion, "Authenticator App");

            addToast("Security setup complete! Your file is now protected.", "success");
            navigate('/portal-dashboard');
        } catch (error) {
            console.error("TOTP Verification Failed:", error);
            addToast("Invalid Authenticator code. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    // 4. Trigger SMS Dispatch
    const handleSendSms = async () => {
        if (!clientPhone) return;
        setLoading(true);

        try {
            setupRecaptcha();
            const user = auth.currentUser;
            const multiFactorSession = await multiFactor(user).getSession();

            const phoneInfoOptions = {
                phoneNumber: clientPhone,
                session: multiFactorSession
            };

            const phoneAuthProvider = new PhoneAuthProvider(auth);
            const verId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, window.recaptchaVerifier);

            setVerificationId(verId);
            setActiveView('sms-verify');
            addToast("Text message sent!", "success");

        } catch (error) {
            console.error("SMS Dispatch Failed:", error);
            // If it fails (e.g., bot detected), reset recaptcha so they can try again
            if (window.recaptchaVerifier) {
                window.recaptchaVerifier.clear();
                window.recaptchaVerifier = null;
            }
            addToast("Failed to send text message. Please try again or use the Authenticator App.", "error");
        } finally {
            setLoading(false);
        }
    };

    // 5. Verify the received SMS Code
    const handleVerifySms = async (e) => {
        e.preventDefault();
        if (verificationCode.length !== 6 || !verificationId) return;

        setLoading(true);
        try {
            const user = auth.currentUser;
            const phoneAuthCredential = PhoneAuthProvider.credential(verificationId, verificationCode);
            const assertion = PhoneMultiFactorGenerator.assertionForEnrollment(phoneAuthCredential);

            await multiFactor(user).enroll(assertion, "SMS Text Message");

            addToast("Security setup complete! Your file is now protected.", "success");
            navigate('/portal-dashboard');
        } catch (error) {
            console.error("SMS Verification Failed:", error);
            addToast("Invalid SMS code. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    if (initializing) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-pulse text-gray-500 font-medium">Generating secure keys...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8 font-sans">
            {/* The Invisible reCAPTCHA container needed by Firebase */}
            <div id="recaptcha-container"></div>

            <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
                <div className="mx-auto h-16 w-16 bg-blue-600 rounded-2xl shadow-lg flex items-center justify-center mb-4">
                    <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                </div>
                <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">Secure Your Account</h2>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 px-4">
                    {activeView === 'totp' && "Scan the QR code with an authenticator app (like Google Authenticator) to complete setup."}
                    {activeView === 'sms-options' && "We can send a secure verification code to the phone number listed on your file."}
                    {activeView === 'sms-verify' && `Enter the 6-digit code sent to ${maskedPhone}`}
                </p>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow-xl sm:rounded-2xl sm:px-10 border border-gray-100 dark:border-gray-700">

                    {/* VIEW 1: AUTHENTICATOR APP (DEFAULT) */}
                    {activeView === 'totp' && (
                        <>
                            <div className="flex justify-center mb-8 p-4 bg-white rounded-xl border-2 border-dashed border-gray-200">
                                {qrCodeUrl && <QRCode value={qrCodeUrl} size={200} level="M" />}
                            </div>

                            <form className="space-y-6" onSubmit={handleVerifyTotp}>
                                <div>
                                    <input
                                        type="text" inputMode="numeric" pattern="[0-9]*" maxLength="6" required
                                        value={verificationCode} onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                                        className="appearance-none block w-full px-4 py-3 text-center text-2xl tracking-widest border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
                                        placeholder="000000"
                                    />
                                </div>
                                <button
                                    type="submit" disabled={loading || verificationCode.length !== 6}
                                    className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 transition-all ${loading || verificationCode.length !== 6 ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {loading ? 'Verifying...' : 'Verify & Secure Account'}
                                </button>
                            </form>

                            <div className="mt-6 text-center">
                                <button
                                    onClick={() => { setActiveView('sms-options'); setVerificationCode(''); }}
                                    className="text-sm font-medium text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                                >
                                    Having trouble? Use a text message instead.
                                </button>
                            </div>
                        </>
                    )}

                    {/* VIEW 2: SMS LAUNCH PAD */}
                    {activeView === 'sms-options' && (
                        <div className="space-y-6 text-center">
                            <div className="bg-gray-50 dark:bg-gray-700/50 p-6 rounded-xl border border-gray-200 dark:border-gray-600">
                                <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>

                                {maskedPhone ? (
                                    <>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Phone Number on File:</p>
                                        <p className="text-xl font-bold text-gray-900 dark:text-white tracking-widest">{maskedPhone}</p>
                                    </>
                                ) : (
                                    <p className="text-sm text-red-600 dark:text-red-400 font-medium">No valid phone number found on your file.</p>
                                )}
                            </div>

                            <button
                                onClick={handleSendSms} disabled={loading || !maskedPhone}
                                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 transition-all ${loading || !maskedPhone ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {loading ? 'Sending...' : 'Send Text Message'}
                            </button>

                            <button onClick={() => setActiveView('totp')} className="w-full text-sm font-medium text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200">
                                Go back to Authenticator App
                            </button>

                            {!maskedPhone && (
                                <p className="text-xs text-gray-400 mt-4">Please contact your preparer to update the phone number on your file.</p>
                            )}
                        </div>
                    )}

                    {/* VIEW 3: SMS VERIFICATION */}
                    {activeView === 'sms-verify' && (
                        <form className="space-y-6" onSubmit={handleVerifySms}>
                            <div>
                                <input
                                    type="text" inputMode="numeric" pattern="[0-9]*" maxLength="6" required autoFocus
                                    value={verificationCode} onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                                    className="appearance-none block w-full px-4 py-3 text-center text-2xl tracking-widest border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
                                    placeholder="000000"
                                />
                            </div>
                            <button
                                type="submit" disabled={loading || verificationCode.length !== 6}
                                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 transition-all ${loading || verificationCode.length !== 6 ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {loading ? 'Verifying...' : 'Verify Code'}
                            </button>

                            <button type="button" onClick={() => { setActiveView('sms-options'); setVerificationCode(''); }} className="w-full text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300">
                                Cancel
                            </button>
                        </form>
                    )}

                </div>
            </div>
        </div>
    );
};

export default MfaEnrollment;